import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import {
  useEliteReservation,
  useEliteStructure,
} from "@/contexts/EliteTemporaryContextProvider";
import { useGuestAuth } from "@/contexts/GuestAuthContext";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { ArrowLeft, Award, Building2, Hotel, LogIn } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function GuestAreaLogin() {
  const { config, profile, isLoading, login } = useGuestAuth();
  const { reservation } = useEliteReservation();
  const { structure } = useEliteStructure();
  const navigate = useNavigate();

  useDocumentTitle("Accedi alla Guest Area");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (profile) {
      navigate("/guest-area", { replace: true });
    }
  }, [profile, navigate]);

  useEffect(() => {
    const resObj = (reservation as any)?.reservation;
    const firstCheckedIn = (reservation as any)?.alreadyCheckedInGuests?.[0];
    const emailCandidate = firstCheckedIn?.email || resObj?.email || "";
    if (!email && emailCandidate) {
      setEmail(emailCandidate);
    }
  }, [reservation]);

  if (isLoading && !config) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex flex-col items-center justify-center py-16 px-4 text-center gap-3"
      >
        <Spinner className="size-7 text-primary" />
        <p className="text-sm font-medium text-muted-foreground">
          Caricamento Guest Area in corso...
        </p>
      </div>
    );
  }

  if (!config?.isEnabled) {
    return (
      <div className="max-w-lg mx-auto py-12 px-4">
        <Card className="border border-border bg-card shadow-none">
          <CardHeader className="space-y-1">
            <CardTitle className="text-xl font-bold">
              Guest Area non attiva
            </CardTitle>
            <CardDescription className="text-sm leading-relaxed">
              La Guest Area non è attualmente abilitata per questa struttura.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild variant="outline" className="min-h-11">
              <Link to="/">Torna alla Home</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const scopeTitle = config.isOrganizationCentralized
    ? config.organizationName || structure?.name || "Gruppo Hotel"
    : structure?.name || config.structureName || "Hotel";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!email.trim() || !password) {
      setErrorMessage("Inserisci indirizzo email e password per continuare.");
      return;
    }

    setSubmitting(true);
    try {
      const outcome = await login(email.trim(), password);
      if (outcome.requiresVerification) {
        toast.info(
          outcome.message ||
            "Conferma il tuo indirizzo email tramite il codice OTP o il link che ti abbiamo inviato.",
        );
        const sid = outcome.structureId || config.structureId;
        const targetEmail = outcome.email || email.trim();
        navigate(
          `/guest-area/verify?structureId=${sid}&email=${encodeURIComponent(
            targetEmail,
          )}`,
        );
        return;
      }
      toast.success(`Bentornato in ${config.programName}`);
      navigate("/guest-area", { replace: true });
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        "Credenziali non valide.";
      setErrorMessage(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto py-4 sm:py-8 space-y-6">
      <div>
        <Button
          asChild
          variant="ghost"
          className="min-h-11 px-3 -ml-3 text-muted-foreground hover:text-foreground gap-2"
        >
          <Link to="/">
            <ArrowLeft className="size-4" aria-hidden="true" />
            <span>Torna alla Home</span>
          </Link>
        </Button>
      </div>

      <section aria-labelledby="login-heading" className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-primary">
            {config.programName}
          </span>
          <Badge variant="secondary" className="text-xs font-medium gap-1">
            {config.isOrganizationCentralized ? (
              <>
                <Building2 className="size-3" aria-hidden="true" />
                <span>Gruppo {scopeTitle}</span>
              </>
            ) : (
              <>
                <Hotel className="size-3" aria-hidden="true" />
                <span>{scopeTitle}</span>
              </>
            )}
          </Badge>
        </div>

        <h1
          id="login-heading"
          className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground"
        >
          Accedi alla Guest Area
        </h1>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {config.isOrganizationCentralized
            ? `Accedi con il tuo account per consultare il saldo punti e i movimenti registrati nelle strutture del gruppo ${scopeTitle}.`
            : `Accedi con il tuo account dedicato a ${scopeTitle} per visualizzare la tua tessera fedeltà, i punti e le tue prenotazioni.`}
        </p>
      </section>

      <Card className="border border-border bg-card shadow-none">
        <CardContent className="p-5 sm:p-6">
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {errorMessage && (
              <div
                role="alert"
                className="rounded-lg border border-destructive/40 bg-destructive/10 px-3.5 py-2.5 text-xs sm:text-sm font-medium text-destructive"
              >
                {errorMessage}
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="guest-login-email">Email</Label>
              <Input
                id="guest-login-email"
                type="email"
                autoComplete="email"
                placeholder="email@esempio.it"
                className="min-h-11"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="guest-login-password">Password</Label>
              <Input
                id="guest-login-password"
                type="password"
                autoComplete="current-password"
                placeholder="Inserisci la tua password"
                className="min-h-11"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <Button
              type="submit"
              className="w-full min-h-11 font-semibold gap-2 cursor-pointer"
              disabled={submitting}
            >
              <LogIn className="size-4" aria-hidden="true" />
              <span>
                {submitting ? "Accesso in corso..." : "Accedi al tuo account"}
              </span>
            </Button>
          </form>
        </CardContent>
      </Card>

      <div className="rounded-xl border border-border bg-muted/20 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <Award
            className="size-5 text-primary shrink-0 mt-0.5"
            aria-hidden="true"
          />
          <div className="space-y-0.5">
            <p className="text-sm font-semibold text-foreground">
              Non hai ancora una tessera?
            </p>
            <p className="text-xs text-muted-foreground">
              Attiva gratuitamente il tuo account per accumulare punti.
            </p>
          </div>
        </div>
        <Button
          asChild
          variant="outline"
          className="min-h-11 px-4 shrink-0 shadow-none"
        >
          <Link to="/guest-area/register">Registrati</Link>
        </Button>
      </div>
    </div>
  );
}
